import graphql__96e4e939161829b8f0d5 from "./__generated__/ConfigureAccount_user.graphql.js";import { useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import { defineMessages } from "react-intl";

import withFormStore from "../Form/withFormStore";
import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import StringField from "../Form/Field/StringField";
import PasswordField from "../Form/Field/PasswordField";
import Button from "../Buttons/Button";
import { useNavigateTo } from "../Routes/RoutesContext";
import { useFormatMessage } from "../Intl/Context";
import Answers from "../Answer/Answers";
import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";
import { intlMessages as profileLabels } from "./ProfileEditionForm";

import { configureAccount } from "../../mutations/ConfigureAccountMutation";
import { setViewer } from "../../reducers/viewer";
import { unsetStoredLocation } from "../../reducers/location";

const ConfigureAccount = props => {
  const user = useFragment(ConfigureAccount_user, props.user);
  const { name, answers } = user;
  const { formState, pushErrors, validateForm } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert } = useAlert();

  const storedLocation = useSelector(({ location }) => location.storedLocation?.pathname);
  const navigateTo = useNavigateTo();
  const dispatch = useDispatch();

  const t = useFormatMessage();
  const labels = {
    title: t(intlMessages.title),
    customFieldsSectionTitle: t(profileLabels.customFieldsSectionTitle),
    description: t(intlMessages.description),
    requiredFieldsHint: t(formPageIntlMessages.requiredFieldsHint),
    name: t({ id: "profile.form.name" }),
    email: t({ id: "profile.form.email" }),
    password: t({ id: "profile.form.password" }),
    passwordHint: t({ id: "profile.form.passwordHint" }),
    passwordConfirmation: t({
      id: "profile.form.passwordConfirmation"
    }),
    update: t(intlMessages.update),
    success: t(intlMessages.success)
  };

  const goToStoredLocation = useCallback(() => {
    const targetLocation = storedLocation || "/";
    navigateTo(targetLocation);
    dispatch(unsetStoredLocation());
  }, [dispatch, navigateTo, storedLocation]);

  const onUpdateResponse = useCallback(
    response => {
      setIsSubmitting(false);

      if (response.configureAccount.success) {
        showAlert({
          message: labels.success,
          type: ALERT_TYPE_SUCCESS
        });

        dispatch(setViewer(response.configureAccount.user));
        goToStoredLocation();
      } else {
        pushErrors(response.configureAccount.errors);

        showAlert({
          message: response.configureAccount.notice,
          type: ALERT_TYPE_ERROR
        });
      }
    },
    [dispatch, goToStoredLocation, labels.success, pushErrors, showAlert]
  );

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      if (!validateForm()) return;

      setIsSubmitting(true);
      configureAccount(formState, onUpdateResponse);
    },
    [formState, onUpdateResponse, validateForm]
  );

  return (
    <div className="profile container">
      <div className="box padding-lg">
        <div className="row">
          <div className="col-12">
            <div className="text-subtitle-page margin-bottom-md">{labels.title}</div>

            <div className="margin-bottom-md">{labels.description}</div>

            <form onSubmit={onSubmit}>
              <p
                className="form-hint-required-fields margin-bottom-sm text-small"
                dangerouslySetInnerHTML={{ __html: labels.requiredFieldsHint }}
              />

              <div className="row">
                <div className="col-md-12">
                  <StringField
                    fieldName="name"
                    label={labels.name}
                    required
                    defaultValue={name}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <PasswordField
                    fieldName="password"
                    label={labels.password}
                    hint={labels.passwordHint}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="col-md-6">
                  <PasswordField
                    fieldName="passwordConfirmation"
                    label={labels.passwordConfirmation}
                    required
                    confirmationOf="password"
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {answers.length > 0 && (
                <>
                  <hr />

                  <h3 className="text-subtitle-page margin-bottom-sm">
                    {labels.customFieldsSectionTitle}
                  </h3>

                  <Answers answers={answers} />
                </>
              )}

              <hr />

              <Button
                type="submit"
                className="btn btn-primary btn-block-md"
                label={labels.update}
                iconClassName="icon-validate"
                iconPosition="after"
                disabled={isSubmitting}
                data-loading={isSubmitting}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfigureAccount_user = graphql__96e4e939161829b8f0d5;

export default withFormStore(ConfigureAccount);

const intlMessages = defineMessages({
  title: {
    id: "profile.configuration.title",
    defaultMessage: "Complétez votre profil avant de continuer"
  },
  description: {
    id: "profile.configuration.description",
    defaultMessage: `
      Vous pouvez ici choisir un nouveau pseudonyme pour améliorer votre
      anonymat ou au contraire mettre votre vrai nom, et modifier votre mot de
      passe afin de choisir un mot de passe dont vous vous rappelerez.
    `
  },
  update: {
    id: "profile.configuration.update",
    defaultMessage: "Valider mon profil et accéder à la plateforme"
  },
  success: {
    id: "profile.configuration.success",
    defaultMessage: "Votre profil a bien été mis à jour et validé !"
  }
});
